import _ from 'lodash';
import { AssessmentQuality, ExamScoringSchema, ExamTask } from '../models/assessment.types';
import { CriteriaSetupResponseDto } from '../models/session.types';
import { ExaminerFunction } from '../pages/Assessment/Assessment.types';
import { Status } from '../services/exams/assessmentSession/assessments.types';

export const isCommentForScoreRequired = (score: number | null, criterion: ExamScoringSchema | null): boolean => {
  return !!criterion?.examScoreValues.find((e) => e.score === score && e.isCommentRequired);
};

export const getCriterionFromTask = (schemaId: string, task: ExamTask): ExamScoringSchema | null => {
  const foundSchema = task.examScoringSchemas.find((schema) => schema.examScoringSchemaId === schemaId);
  return foundSchema || null;
};

export const getCriterionFromTasks = (tasks: ExamTask[], schemaId: string): ExamScoringSchema | null => {
  const foundSchema = tasks
    .map((task) => task.examScoringSchemas.find((schema) => schema.examScoringSchemaId === schemaId))
    .filter((schema) => schema)
    .pop();
  return foundSchema || null;
};

export const getTaskFromTasks = (tasks: ExamTask[], taskId: string): ExamTask | null => {
  const foundTask = tasks.find((task) => task.taskId === taskId);
  return foundTask || null;
};

export const canUserAssessAfterSendingBack = (
  userFunction: ExaminerFunction,
  examTaskGroupSolutionScoreState: Status,
  isControlExamTaskGroupSolution: boolean,
) => {
  return (
    (userFunction === ExaminerFunction.CZE && examTaskGroupSolutionScoreState === Status.Assessed) ||
    ([ExaminerFunction.KK, ExaminerFunction.KGZ].includes(userFunction) &&
      examTaskGroupSolutionScoreState === Status.Closed &&
      !isControlExamTaskGroupSolution)
  );
};

export const getLeadingCriterionIdForDependentCriterion = (
  criteriaSetups: CriteriaSetupResponseDto[] | null,
  criterion: ExamScoringSchema,
): string[] => {
  return criteriaSetups
    ? _.uniq(
        criteriaSetups
          .filter((e) => e.dependentSchemaId === criterion.examScoringSchemaId)
          .map((item) => item.leadingSchemaId),
      )
    : [];
};

export const getLeadingCriterionsForCriterion = (
  criterion: ExamScoringSchema,
  criteriaSetups: CriteriaSetupResponseDto[] | null,
  task: ExamTask,
): ExamScoringSchema[] => {
  const leadingCriterionsIds = getLeadingCriterionIdForDependentCriterion(criteriaSetups, criterion);
  return task.examScoringSchemas.filter((e) => leadingCriterionsIds.includes(e.examScoringSchemaId));
};

export const getLeadingCriterionsNames = (
  criteriaSetups: CriteriaSetupResponseDto[] | null,
  task: ExamTask,
  criterion: ExamScoringSchema,
): string[] => {
  return getLeadingCriterionsForCriterion(criterion, criteriaSetups, task).map((item) => item.criterionName);
};

export const areAllLeadingCriterionsAssessed = (
  criteriaSetups: CriteriaSetupResponseDto[] | null,
  criterion: ExamScoringSchema,
  task: ExamTask,
): boolean => {
  const leadingCriterionsIds = getLeadingCriterionIdForDependentCriterion(criteriaSetups, criterion);
  return !task.examScoringSchemas.find((e) => leadingCriterionsIds.includes(e.examScoringSchemaId) && e.score === null);
};
